import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Upload,
  Spin,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import useUsers from "../../hooks/users/useUsers";
import useOrgHierarchy from "../../hooks/projects/useOrgHierarchy";
import "./AddProjectModal.css";

const { Option } = Select;

function AddProjectModal({
  open,
  onCancel,
  onSubmit,
  loading: submitLoading,
}) {
  const [form] = Form.useForm();

  // Hooks
  const { users, loading: usersLoading, fetchUsers } = useUsers();
  const { zonesList, loading: hierarchyLoading } = useOrgHierarchy();

  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open, fetchUsers]);

  const handleFinish = async (values) => {
    try {
      setUploading(true);
      const formData = new FormData();

      formData.append("name", values.name);
      formData.append("zone_name", values.zone_name);        // Circle code
      formData.append("assigned_engineer", values.assigned_engineer ?? 0);

      if (values.image?.fileList?.[0]?.originFileObj) {
        formData.append("image", values.image.fileList[0].originFileObj);
      }

      const result = await onSubmit(formData);

      if (result.success) {
        message.success("Project created successfully");
        form.resetFields();
        onCancel();
      } else {
        message.error(result.error || "Failed to create project");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  const getUserLabel = (user) => {
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    const role = user.role?.replace(/_/g, " ") || "User";
    return fullName
      ? `${fullName} (${role})`
      : user.username || "Unknown User";
  };

  return (
    <Modal
      title="Create New Scheme"
      open={open}
      closable={false}
      onCancel={onCancel}
      footer={null}
      width={580}
      height={100}
      destroyOnClose
      className="project-modal"
    >
      <Spin spinning={usersLoading || hierarchyLoading} tip="Loading data...">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="project-form"
        >
          <div className="form-section">
            <div className="form-section-title">Basic Information</div>

            <Form.Item
              label="Project Name"
              name="name"
              rules={[{ required: true, message: "Please enter project name" }]}
            >
              <Input placeholder="e.g. 220KV Panvel Substation" size="large" />
            </Form.Item>

            <Form.Item
              label="Zone"
              name="zone_name"
              rules={[{ required: true, message: "Please select zone" }]}
            >
              <Select
                placeholder="Select Zone"
                size="large"
                showSearch
                optionFilterProp="children" 
                loading={hierarchyLoading}
              >
                {zonesList.map((zone) => (
                  <Option key={zone.name} value={zone.name}>
                    {zone.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Project Image"
              name="image"
              valuePropName="file"
              getValueFromEvent={(e) => e}
            >
              <Upload
                beforeUpload={() => false}
                maxCount={1}
                accept="image/*"
                listType="picture"
              >
                <Button icon={<UploadOutlined />} size="large">
                  Click to Upload Image
                </Button>
              </Upload>
            </Form.Item>
          </div>

          <div className="form-section">
            <div className="form-section-title">Assignment</div>

            <Form.Item
              label="Assigned Engineer"
              name="assigned_engineer"
              rules={[{ required: true, message: "Please select an engineer" }]}
            >
              <Select
                placeholder="Search and select engineer"
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                size="large"
                loading={usersLoading}
              >
                {users.map((user) => (
                  <Option
                    key={user.id}
                    value={user.id}
                    label={getUserLabel(user)}
                  >
                    {getUserLabel(user)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="modal-footer-buttons">
            <Button size="large" className="project-cancel-btn" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitLoading || uploading}
              className="project-submit-btn"
            >
              Create Scheme
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
}

export default AddProjectModal;